import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    MenuItem,
    IconButton,
    Alert,
    Divider,
    InputAdornment,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { API_URL } from '../../api';
import YearGroupSelect from '../../components/YearGroupSelect';
import TermSelect from '../../components/TermSelect';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

export default function InvoiceScheduleCreatePage() {
    const navigate = useNavigate();
    const [recipientSource, setRecipientSource] = useState('account');
    const [recipients, setRecipients] = useState([]);
    const [enquiries, setEnquiries] = useState([]);
    const [parentId, setParentId] = useState('');
    const [contactEnquiryId, setContactEnquiryId] = useState('');
    const [recipientEmail, setRecipientEmail] = useState('');
    const [recipientName, setRecipientName] = useState('');
    const [kind, setKind] = useState('recurring');
    const [cadence, setCadence] = useState('monthly');
    const [startAt, setStartAt] = useState('');
    const [term, setTerm] = useState('');
    const [yearGroup, setYearGroup] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('both');
    const [bankDetails, setBankDetails] = useState('');
    const [items, setItems] = useState([{ description: '', qty: 1, unitAmount: 0 }]);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        Promise.all([
            axios.get(`${API_URL}/api/admin/accounts?role=parent&limit=200`, authHeaders()),
            axios.get(`${API_URL}/api/admin/accounts?role=student&limit=200`, authHeaders()),
        ])
            .then(([parentsRes, studentsRes]) => {
                const parents = (parentsRes.data?.items || []).map((p) => ({ ...p, _role: 'Parent' }));
                const students = (studentsRes.data?.items || []).map((s) => ({ ...s, _role: 'Student' }));
                setRecipients([...parents, ...students]);
            })
            .catch(() => setRecipients([]));

        axios
            .get(`${API_URL}/api/admin/contact-enquiries?limit=200`, authHeaders())
            .then((res) => setEnquiries(res.data?.items || []))
            .catch(() => setEnquiries([]));
    }, []);

    const updateItem = (idx, key, value) =>
        setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));
    const addItem = () => setItems((arr) => [...arr, { description: '', qty: 1, unitAmount: 0 }]);
    const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

    const recipientReady =
        (recipientSource === 'account' && parentId) ||
        (recipientSource === 'enquiry' && contactEnquiryId) ||
        (recipientSource === 'inline' && recipientEmail.trim());

    const dateReady = kind === 'oneOff' ? !!startAt : true;

    const create = async () => {
        setBusy(true);
        setError('');
        try {
            const payload = {
                kind,
                cadence: kind === 'recurring' ? cadence : 'monthly',
                paymentMethod,
                bankDetails,
                startAt: startAt || undefined,
                term: term || undefined,
                yearGroup: yearGroup || undefined,
                lineItemsTemplate: items
                    .filter((i) => String(i.description || '').trim())
                    .map((i) => ({
                        description: String(i.description).trim(),
                        qty: Number(i.qty) || 1,
                        unitAmount: Number(i.unitAmount) || 0,
                    })),
            };
            if (recipientSource === 'account') {
                payload.parentId = parentId;
            } else if (recipientSource === 'enquiry') {
                payload.contactEnquiryId = contactEnquiryId;
            } else {
                payload.recipientEmail = recipientEmail.trim();
                payload.recipientName = recipientName.trim();
            }
            await axios.post(
                `${API_URL}/api/admin/invoices/schedules`,
                payload,
                authHeaders()
            );
            navigate('/invoice-schedules');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Container>
            <Typography variant="h4" sx={{ mb: 3 }}>
                {kind === 'oneOff' ? 'Schedule a one-off invoice' : 'Create a recurring schedule'}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Card sx={{ p: 3, mb: 3 }}>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Schedule type
                        </Typography>
                        <ToggleButtonGroup
                            value={kind}
                            exclusive
                            onChange={(_, v) => v && setKind(v)}
                            size="small"
                        >
                            <ToggleButton value="oneOff">One-off (single future invoice)</ToggleButton>
                            <ToggleButton value="recurring">Recurring</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Recipient source
                        </Typography>
                        <ToggleButtonGroup
                            value={recipientSource}
                            exclusive
                            onChange={(_, v) => v && setRecipientSource(v)}
                            size="small"
                        >
                            <ToggleButton value="account">Registered account</ToggleButton>
                            <ToggleButton value="enquiry">Contact enquiry</ToggleButton>
                            <ToggleButton value="inline">Enter manually</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {recipientSource === 'account' && (
                        <TextField
                            select
                            size="small"
                            label="Parent / Student"
                            value={parentId}
                            onChange={(e) => setParentId(e.target.value)}
                        >
                            <MenuItem value="">— Select recipient —</MenuItem>
                            {recipients.map((r) => (
                                <MenuItem key={r._id} value={r._id}>
                                    {r._role}: {r.firstName} {r.surname} ({r.email})
                                </MenuItem>
                            ))}
                        </TextField>
                    )}

                    {recipientSource === 'enquiry' && (
                        <TextField
                            select
                            size="small"
                            label="Contact enquiry"
                            value={contactEnquiryId}
                            onChange={(e) => setContactEnquiryId(e.target.value)}
                            helperText="Each scheduled run uses the parent's name + email from this enquiry."
                        >
                            <MenuItem value="">— Select enquiry —</MenuItem>
                            {enquiries.map((q) => (
                                <MenuItem key={q._id} value={q._id}>
                                    {new Date(q.createdAt).toLocaleDateString('en-GB')} · {q.firstName} {q.surname} ({q.email}) · {q.enquiryType}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}

                    {recipientSource === 'inline' && (
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Recipient name"
                                value={recipientName}
                                onChange={(e) => setRecipientName(e.target.value)}
                                size="small"
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                label="Recipient email"
                                value={recipientEmail}
                                onChange={(e) => setRecipientEmail(e.target.value)}
                                size="small"
                                required
                                sx={{ flex: 1 }}
                            />
                        </Stack>
                    )}

                    <Stack direction="row" spacing={2}>
                        <TextField
                            type="date"
                            label={kind === 'oneOff' ? 'Send date' : 'First run date'}
                            InputLabelProps={{ shrink: true }}
                            value={startAt}
                            onChange={(e) => setStartAt(e.target.value)}
                            size="small"
                            required={kind === 'oneOff'}
                            sx={{ flex: 1 }}
                        />
                        {kind === 'recurring' && (
                            <TextField
                                select
                                size="small"
                                label="Repeat every"
                                value={cadence}
                                onChange={(e) => setCadence(e.target.value)}
                                sx={{ flex: 1 }}
                            >
                                <MenuItem value="weekly">Week</MenuItem>
                                <MenuItem value="fortnightly">Fortnight (2 weeks)</MenuItem>
                                <MenuItem value="monthly">Month</MenuItem>
                                <MenuItem value="quarterly">Term (3 months)</MenuItem>
                                <MenuItem value="yearly">Year</MenuItem>
                            </TextField>
                        )}
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <TermSelect
                            value={term}
                            onChange={setTerm}
                            allowAny
                            anyLabel="— None —"
                            helperText="Set on every generated invoice (optional)."
                            sx={{ flex: 1 }}
                        />
                        <YearGroupSelect
                            value={yearGroup}
                            onChange={setYearGroup}
                            allowAny
                            anyLabel="— None —"
                            helperText="Used for filtering. Auto-pulled from registered accounts if left blank."
                            sx={{ flex: 1 }}
                        />
                    </Stack>

                    <TextField
                        select
                        size="small"
                        label="Payment method"
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                        <MenuItem value="both">Stripe + bank</MenuItem>
                        <MenuItem value="stripe">Stripe only</MenuItem>
                        <MenuItem value="bank">Bank only</MenuItem>
                    </TextField>
                    {(paymentMethod === 'bank' || paymentMethod === 'both') && (
                        <TextField
                            multiline
                            minRows={2}
                            label="Bank details"
                            value={bankDetails}
                            onChange={(e) => setBankDetails(e.target.value)}
                        />
                    )}

                    <Divider />
                    <Typography variant="subtitle2">Line items (template)</Typography>
                    {items.map((it, idx) => (
                        <Stack direction="row" spacing={1} key={idx}>
                            <TextField
                                size="small"
                                label="Description"
                                value={it.description}
                                onChange={(e) => updateItem(idx, 'description', e.target.value)}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                size="small"
                                type="number"
                                label="Qty"
                                value={it.qty}
                                onChange={(e) => updateItem(idx, 'qty', e.target.value)}
                                sx={{ width: 80 }}
                                inputProps={{ min: 1, step: 1 }}
                            />
                            <TextField
                                size="small"
                                type="number"
                                label="Unit price"
                                value={it.unitAmount}
                                onChange={(e) => updateItem(idx, 'unitAmount', e.target.value)}
                                sx={{ width: 160 }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                }}
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                            <IconButton onClick={() => removeItem(idx)} disabled={items.length === 1}>
                                <DeleteIcon />
                            </IconButton>
                        </Stack>
                    ))}
                    <Button onClick={addItem} startIcon={<AddIcon />}>
                        Add line item
                    </Button>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={() => navigate('/invoice-schedules')} disabled={busy}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={create}
                            disabled={busy || !recipientReady || !dateReady}
                        >
                            {kind === 'oneOff' ? 'Schedule one-off invoice' : 'Create recurring schedule'}
                        </Button>
                    </Box>
                </Stack>
            </Card>
        </Container>
    );
}
